import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { Panel, ListGroup, ListGroupItem, Modal, Button } from 'react-bootstrap';

import { IDokument, IDokumenteResponse, IResponse } from '../../models/ApiTypes';
import { bind, inject } from '../../utils/decorators';
import { ApiStore } from '../../stores/ApiStore';
import FaIcon from '../site/FaIcon';

type DocumentSource = 'auftrag' | 'vorgang';

export interface IDocumentListItemProps {
  source: DocumentSource;
  id: string;
  document: IDokument;
}

@observer
export class DocumentListItem extends React.Component<IDocumentListItemProps, {}> {

  @inject(ApiStore) apiStore: ApiStore;

  @observable modalVisible: boolean = false;

  @bind @action showModal() {
    this.modalVisible = true;
  }

  @bind @action hideModal() {
    this.modalVisible = false;
  }

  render() {
    let { document, source, id } = this.props;
    let downloadUrl;
    switch (source) {
      case 'auftrag':
        downloadUrl = this.apiStore.auftragDokumentDownloadUrl(id, document.eintrag_guid);
        break;
      case 'vorgang':
        downloadUrl = this.apiStore.vorgangDokumentDownloadUrl(id, document.eintrag_guid);
        break;
      default:
        throw new Error(`Unknown Document-Source (${source})`);
    }

    let faIcon;
    switch (document.dateiendung.toLowerCase()) {
      case 'pdf':
        faIcon = 'file-pdf-o';
        break;
      case 'xls':
      case 'xlsx':
        faIcon = 'file-excel-o';
        break;
      case 'doc':
      case 'docx':
        faIcon = 'file-word-o';
        break;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        faIcon = 'file-image-o';
        break;
      case 'msg':
        faIcon = 'envelope-o';
        break;
      default:
        faIcon = 'file';
        break;
    }

    let faIconStatus;
    let hintStatus;
    if (document.docstatus) {
      switch (document.docstatus.toLowerCase()) {
        case 'oeffentl':
          faIconStatus = 'globe';
          hintStatus = 'Dieses Dokument kann über den öffentlichen Link heruntergeladen werden';
          break;
        case 'freigabe':
          faIconStatus = 'download';
          hintStatus = 'Dieses Dokument kann als angemeldeter Benutzer heruntergeladen werden';
          break;
        case 'exterz':
          faIconStatus = 'lock';
          hintStatus = 'Dieses Dokument wurde erfolgreich übermittelt, kann aber nicht mehr heruntergeladen werden';
          break;
        default:
          faIconStatus = undefined;
          hintStatus = undefined;
      }
    }

    const downloadErlaubt = document.downloadfreigegeben ? document.downloadfreigegeben : false;
    if (downloadErlaubt) {
      return (
        <ListGroupItem target="_blank" href={downloadUrl}>
          <FaIcon icon={faIcon} /> {document.erfasstam} - {document.titel} ({document.docid})
          {faIconStatus &&
            <span className="pull-right" title={hintStatus}><FaIcon icon={faIconStatus} /></span>
          }
        </ListGroupItem>
      );
    } else {
      return (
        <ListGroupItem onClick={this.showModal} className="dokument-kein-download">
          <Modal show={this.modalVisible} onHide={this.hideModal} className="modal-vertical-centered">
            <Modal.Header>
              <Modal.Title>Dokument nicht freigegeben</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Das Dokument <b>"{document.titel}"</b> (Aktenzeichen {document.docid}) wurde zur Bearbeitung übermittelt.<br/>
              Die freigegebenen Dokumente finden Sie nach Prüfung durch die DIQ Zert in der Auftragsübersicht.
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={this.hideModal}>Schließen</Button>
            </Modal.Footer>
          </Modal>
          <FaIcon icon={faIcon} /> {document.erfasstam} - {document.titel} ({document.docid})
          {faIconStatus &&
            <span className="pull-right" title={hintStatus}><FaIcon icon={faIconStatus} /></span>
          }
        </ListGroupItem>
      );
    }

  }

}


interface IDocumentListProps {
  source: DocumentSource;
  id: string;
  header?: 'headline' | 'panel';
}

@observer
export default class DocumentList extends React.Component<IDocumentListProps, {}> {

  static defaultProps: Partial<IDocumentListProps> = {
    header: 'panel'
  };

  @observable docs: IDokument[];
  @inject(ApiStore) apiStore: ApiStore;

  async componentDidMount() {
    this.refresh();
  }

  async refresh() {
    let response: IResponse<IDokumenteResponse>;
    switch (this.props.source) {
      case 'vorgang':
        response = await this.apiStore.vorgangDokumente(this.props.id);
        break;
      case 'auftrag':
        response = await this.apiStore.auftragDokumente(this.props.id);
        break;
      default:
        throw new Error(`Unknown Document-Source (${this.props.source})`);
    }
    if (response && response.ok) {
      this.setDocs(response.json);
    }
  }

  @action setDocs(docs: IDokumenteResponse) {
    this.docs = docs;
  }

  stringToDate(datumDE: string) {
    const elemente = datumDE.match(/(\d+)/ig);
    if (elemente) {
      return new Date(parseInt(elemente[2], 10), parseInt(elemente[1], 10) - 1, parseInt(elemente[0], 10),  parseInt(elemente[3], 10),  parseInt(elemente[4], 10),  parseInt(elemente[5], 10));
    } else {
      return undefined;
    }
  }

  @bind datumsvergleich(a: IDokument, b: IDokument) {
    const d1 = this.stringToDate(a.erfasstam);
    const d2 = this.stringToDate(b.erfasstam);
    if (d1 && d2) {
      return d2.getTime() - d1.getTime();
    } else {
      return 0;
    }
  }

  render() {
    const {source, id, header} = this.props;
    const headlineText = 'Dokumente';
    return (
      <div>
        {header === 'headline' && <h3>{headlineText}</h3>}
        <Panel header={header === 'panel' && headlineText}>
          <ListGroup fill={true}>
            {!this.docs ? (
              <ListGroupItem>lade Dokumente...</ListGroupItem>
            ) : (this.docs.length === 0) ? (
              <ListGroupItem>Keine Dokumente vorhanden</ListGroupItem>
            ) : this.docs.sort(this.datumsvergleich).map((doc, index) => <DocumentListItem key={index} document={doc} source={source} id={id} />)}
          </ListGroup>
        </Panel>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/dokumente/DocumentList.tsx